import { useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { hideLoading } from '../../../../reducers/loading'
import Swal from 'sweetalert2'
import { fileReader } from '../../../../helpers/qr-reader'
import Cookies from 'js-cookie'
import Colors from '../../../../constants/Colors'
import styled from 'styled-components'
import { Spinner } from 'react-bootstrap'
import config from '../../../../config'

export const IMAGE_STATUS = {
  UNKNOWN: 'UNKNOWN',
  PENDING: 'PENDING',
  PROCESSED: 'PROCESSED',
  ACTIVE: 'ACTIVE',
  FAILED: 'FAILED',
  DELETED: 'DELETED',
}

const UploadImage = ({
  inputId = 'upload-image',
  title = 'รูปภาพ',
  placeholder = 'กรุณาอัปโหลดรูปภาพ',
  userId,
  onUploadImage,
  onUploadFail,
  onRemoveImage,
}) => {
  const dispatch = useDispatch()

  const [fileName, setFileName] = useState('')
  const [fileSizeIsWarning, setFileSizeIsWarning] = useState(false)
  const [imagePreview, setImagePreview] = useState(null)
  const [isUploading, setIsUploading] = useState(false)

  const imageRef = useRef()

  const clearImageInput = () => {
    if (imageRef.current) {
      imageRef.current.value = ''
    }
  }

  const onSelectImage = async (element) => {
    if (element.target.files?.[0].size > 5000000) {
      dispatch(hideLoading())
      Swal.fire({
        title: 'ไม่สามารถอัปโหลดได้',
        text: 'กรุณาตรวจสอบขนาดไฟล์และประเภทไฟล์ที่รองรับ',
      })
      setImagePreview(null)
      onUploadImage(null)
      setFileName('')
      setFileSizeIsWarning(true)

      return
    }

    setIsUploading(true)

    try {
      const fr = fileReader
      fr.readAsDataURL(element.target.files[0])
      await new Promise((resolve, reject) => {
        fr.onload = () => resolve()
        fr.onerror = () => reject(new Error('Error reading file'))
      })
      const file = element.target.files[0]

      setImagePreview(fr.result)
      setFileName(file.name)
      dispatch(hideLoading())
      setFileSizeIsWarning(false)

      await uploadImage(file, fr.result)
    } catch (error) {
      console.error(error)

      onUploadImage(null)
      dispatch(hideLoading())
      Swal.fire('ไม่สามารถอัปโหลดได้', 'กรุณาอัปโหลดใหม่อีกครั้ง')
      setImagePreview(null)
      setFileName('')
      setFileSizeIsWarning(true)
      onUploadFail?.()
    } finally {
      setIsUploading(false)
    }

    clearImageInput()
  }

  const uploadImage = async (image, preview) => {
    setIsUploading(true)

    const urlParams = new URLSearchParams({
      source: 'KYC',
      userId: userId,
    })
    const urlResponse = await fetch(`${config.apiAdminGateway}/api/v1/admin/file/upload-url?${urlParams.toString()}`, {
      headers: {
        Authorization: `Bearer ${Cookies.get('accessToken')}`,
      },
    }).then((res) => res.json())

    if (urlResponse.status.code !== 0) throw new Error('Cannot get upload url')

    const { id, url } = urlResponse.data

    await fetch(url, {
      method: 'PUT',
      body: image,
    })

    let isImageReady = false

    while (!isImageReady) {
      await new Promise((resolve) => setTimeout(resolve, 1000))

      const checkImageResponse = await fetch(`${config.apiAdminGateway}/api/v1/admin/file/${id}/status`, {
        headers: {
          Authorization: `Bearer ${Cookies.get('accessToken')}`,
        },
      }).then((res) => res.json())

      if (checkImageResponse.status.code !== 0) throw new Error('Cannot get image status')

      if (checkImageResponse.data.status === IMAGE_STATUS.PROCESSED) {
        isImageReady = true
      } else if (checkImageResponse.data.status === IMAGE_STATUS.FAILED) {
        throw new Error('Image processing failed')
      }
    }

    onUploadImage(id, preview)
  }

  return (
    <div>
      <div className="col-12">
        <Title>{title}</Title>
        <input
          disabled={isUploading}
          ref={imageRef}
          type="file"
          id={inputId}
          name="image-file"
          accept="image/jpg, image/jpeg, image/png, image/webp"
          onClick={(event) => (event.target.value = null)}
          onChange={(event) => !isUploading && onSelectImage(event)}
          hidden
        />
        <UploadSlip>
          <FileName warning={false}>
            {imagePreview ? <span className="colorName">{fileName}</span> : placeholder}
          </FileName>
          <ChooseFileLabel warning={fileSizeIsWarning} htmlFor={inputId}>
            เลือกรูปภาพ
          </ChooseFileLabel>
        </UploadSlip>
        {fileSizeIsWarning && (
          <WarningFileSizeTitle>
            ไฟล์ขนาดไม่เกิน 5 Mb. และนามสกุลไฟล์ที่รองรับ : JPG, JPEG, PNG, WEBP
          </WarningFileSizeTitle>
        )}
      </div>
      <div className="col-12" style={{ textAlign: 'center', cursor: 'pointer' }}>
        <BoxQRCode htmlFor={inputId}>
          {imagePreview ? (
            <div style={{ position: 'relative', zIndex: 1, backgroundColor: 'none' }}>
              <ButtonChangeImage>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span className="fa fa-image text-white" opacity=".16" />
                  <span
                    style={{
                      display: 'flex',
                      margin: '0px 8px 0px 8px',
                      color: 'white',
                      fontSize: '16px',
                      lineHeight: '24px',
                      fontWeight: '600',
                    }}
                  >
                    แก้ไขรูปภาพ
                  </span>
                </div>
              </ButtonChangeImage>
              <ButtonDeleteImage
                onClick={(e) => {
                  e.preventDefault()
                  setImagePreview(null)
                  setFileName('')
                  clearImageInput()
                  onRemoveImage?.()
                }}
              >
                <span className="fa fa-close text-white" width="20" height="20" />
              </ButtonDeleteImage>
              <img src={imagePreview} ref={imageRef} alt="preview" style={{ objectFit: 'contain', width: '100%' }} />
            </div>
          ) : (
            <span className="fa fa-image text-white" opacity=".16" />
          )}
          {isUploading && (
            <div
              style={{
                zIndex: '1',
                position: 'absolute',
                inset: '0',
                backgroundColor: 'rgba(0,0,0,0.6)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Spinner animation="border" role="status" style={{ width: '64px', height: '64px' }}>
                <span className="sr-only">Loading...</span>
              </Spinner>
            </div>
          )}
        </BoxQRCode>
      </div>
    </div>
  )
}

export default UploadImage

const Title = styled.span`
  color: ${Colors.DARK};
  font-size: 16px;
  font-weight: 500;
  line-height: 24px; /* 150% */
`

const FileName = styled.span`
  display: flex;
  justify-content: space-between;
  padding: 8px;
  border-radius: 6px 0px 0px 6px;
  border: solid 1px #ddd;
  height: 40px;
  background-color: ${Colors.WHITE};
  width: 100%;
  text-align: start;
  color: ${Colors.GREY};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  .colorName {
    color: ${Colors.BLACK};
  }
  border: ${(props) => (props.warning ? `solid ${Colors.LIGHT_RED}` : '')};
`

const ButtonChangeImage = styled.span`
  top: 15px;
  left: 8px;
  z-index: 1;
  padding: 8px;
  opacity: 0.8;
  border-radius: 6px;
  position: absolute;
  align-items: center;
  cursor: pointer;
  background-color: ${Colors.BLACK};
  display: flex;
`

const ButtonDeleteImage = styled.span`
  top: 15px;
  right: 8px;
  z-index: 1;
  padding: 10px;
  opacity: 0.8;
  border-radius: 6px;
  position: absolute;
  cursor: pointer;
  background-color: ${Colors.BLACK};
  display: flex;
`

const BoxQRCode = styled.label`
  width: 100%;
  overflow: hidden;
  border-radius: 6px;
  border: solid 1px ${Colors.GREY};
  border-style: dashed;
  min-height: 170px;
  height: 100%;
  display: flex;
  position: relative;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  margin-top: 20px;
`

const UploadSlip = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-top: 4px;
`

const ChooseFileLabel = styled.label`
  background-color: ${(props) => (props.warning ? Colors.LIGHT_RED : Colors.LIGHT_RED)};
  color: ${Colors.WHITE};
  padding: 0.5rem;
  margin: 0;
  border-radius: 0px 6px 6px 0px;
  cursor: pointer;
  height: 40px;
  display: flex !important;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  font-size: 14px;
  line-height: 26px;
  white-space: nowrap;
`

const WarningFileSizeTitle = styled.div`
  margin: none;
  padding: none;
  font-size: 12px;
  text-align: left;
  color: ${Colors.DARK_RED};
`
